'use client';

import { forwardRef, useCallback, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { ToastContext } from './context/toast.context';
import { toastRootRecipe } from './styles/toast-root.styles';
import { ToastIcon } from './toast-icon';
import type { ToastRootPropsInterface } from './types/toast-root.types';

export const ToastRoot = forwardRef<HTMLDivElement, ToastRootPropsInterface>(
  (props: ToastRootPropsInterface, forwardedRef) => {
    const {
      tone = 'info',
      children,
      className,
      css: cssProp,
      isOpen,
      onClose,
      role = 'status',
      ...rest
    } = props;

    const styles = css(toastRootRecipe.raw({ tone }), cssProp);

    const [openState, setOpenState] = useControllableValue<boolean | undefined>({
      controlledValue: isOpen,
      uncontrolledValue: true,
      isControlled: 'isOpen' in props,
      isUncontrolled: false,
      componentName: 'Toast.Root',
      controlledPropName: 'isOpen',
    });

    const handleClose = useCallback(() => {
      setOpenState(false);
      onClose?.();
    }, [onClose, setOpenState]);

    const contextValue = useMemo(() => ({ tone, handleClose }), [tone, handleClose]);

    if (!openState) {
      return null;
    }

    return (
      <ToastContext.Provider value={contextValue}>
        <div
          className={cx('c11n', styles, className)}
          data-c11n-component="Toast.Root"
          data-c11n-version={__C11N_VERSION__}
          ref={forwardedRef}
          role={role}
          {...rest}
        >
          <ToastIcon />
          {children}
        </div>
      </ToastContext.Provider>
    );
  },
);

ToastRoot.displayName = 'Toast.Root';
